// Timestamp lists ("chapters") in YouTube descriptions. Kept free of imports so it can be tested
// with plain `node`.
//
// YouTube shows chapters when a description has a list of timestamps that starts at 0:00, has at
// least three entries in ascending order, and no chapter shorter than 10 seconds
// (support.google.com/youtube/answer/9884579). The server (generateVideoChapters) enforces that
// on what it returns; this file only formats the list and merges it into a description.

export interface VideoChapter { start: number; title: string }

const HEADINGS = ['⏱ Mốc thời gian:', '⏱ Chapters:'];
const TIMESTAMP_LINE = /^\s*(?:\d{1,2}:)?\d{1,2}:\d{2}\b/;
/** YouTube's limit for a description. */
export const MAX_DESCRIPTION_LENGTH = 5000;

/** "0:00 Title" lines under a heading. */
export function formatChaptersForDescription(chapters: VideoChapter[], vn: boolean): string {
    const long = chapters[chapters.length - 1].start >= 3600;
    const fmt = (t: number) => {
        const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), sec = String(Math.floor(t % 60)).padStart(2, '0');
        return long ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
    };
    return [vn ? HEADINGS[0] : HEADINGS[1], ...chapters.map(c => `${fmt(c.start)} ${c.title}`)].join('\n');
}

/** How a description stands with respect to chapters. */
export function chapterState(description: string): 'none' | 'ours' | 'own' {
    const lines = description.split('\n');
    if (lines.some(l => HEADINGS.includes(l.trim()))) return 'ours';
    // A hand-written list (no heading of ours): leave it alone.
    return lines.filter(l => TIMESTAMP_LINE.test(l)).length >= 3 ? 'own' : 'none';
}

/**
 * Puts a chapter block into an existing description and changes nothing else:
 *  - a block added earlier by StudyBoard is replaced in place;
 *  - otherwise the block goes just above the StudyBoard footer (notebook link / "Recorded with"),
 *    or above a trailing hashtag line, or at the end.
 * Returns null when the description has its own hand-written timestamp list, or when the result
 * would exceed YouTube's length limit — the caller then skips the video.
 */
export function mergeChaptersIntoDescription(description: string, block: string): string | null {
    const state = chapterState(description);
    if (state === 'own') return null;
    const paragraphs = description.replace(/\r/g, '').split(/\n{2,}/).map(p => p.replace(/\s+$/, '')).filter(p => p.trim());
    let merged: string[];
    if (state === 'ours') {
        merged = paragraphs.map(p => (HEADINGS.includes(p.split('\n')[0].trim()) ? block : p));
    } else {
        const isFooter = (p: string) => /^(📝 Notebook notes:|🎓 Recorded with StudyBoard)/.test(p.trimStart());
        const isHashtags = (p: string) => /^(#[^\s#]+\s*)+$/.test(p.trim());
        let at = paragraphs.findIndex(isFooter);
        if (at === -1 && paragraphs.length && isHashtags(paragraphs[paragraphs.length - 1])) at = paragraphs.length - 1;
        if (at === -1) at = paragraphs.length;
        merged = [...paragraphs.slice(0, at), block, ...paragraphs.slice(at)];
    }
    const out = merged.join('\n\n');
    return out.length <= MAX_DESCRIPTION_LENGTH ? out : null;
}

/**
 * Chapters already written in a description — the StudyBoard block, or any hand-written list of
 * three or more "m:ss Title" lines. Used to offer one-tap ranges when trimming a video.
 */
export function parseChaptersFromDescription(description: string): VideoChapter[] {
    const out: VideoChapter[] = [];
    for (const line of description.replace(/\r/g, '').split('\n')) {
        const m = line.match(/^\s*(?:(\d{1,2}):)?(\d{1,2}):(\d{2})\s+[-–—]?\s*(.+?)\s*$/);
        if (!m) continue;
        out.push({ start: (+(m[1] || 0)) * 3600 + (+m[2]) * 60 + (+m[3]), title: m[4] });
    }
    out.sort((a, b) => a.start - b.start);
    return out.length >= 3 ? out : [];
}

/** "[m:ss] text" lines of a timed transcript (services/youtubeService parseCaptionToTimedText). */
export function parseTimedTranscript(timed: string): { start: number; text: string }[] {
    const out: { start: number; text: string }[] = [];
    for (const line of timed.split('\n')) {
        const m = line.match(/^\[(\d+):(\d{2})\]\s*(.*)$/);
        if (m) out.push({ start: (+m[1]) * 60 + (+m[2]), text: m[3] });
    }
    return out;
}

/** 83.4 → "1:23"; with tenths: "1:23.4". */
export function formatClock(secs: number, tenths = false): string {
    const s = Math.max(0, secs);
    const whole = Math.floor(s);
    const h = Math.floor(whole / 3600), m = Math.floor((whole % 3600) / 60), sec = String(whole % 60).padStart(2, '0');
    const base = h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
    return tenths ? `${base}.${Math.floor((s - whole) * 10)}` : base;
}

/** "1:23", "1:23.5", "83" or "1:02:03" → seconds; null when it is not a time. */
export function parseClock(text: string): number | null {
    const t = text.trim();
    if (!/^\d+(?::\d{1,2}){0,2}(?:[.,]\d+)?$/.test(t)) return null;
    const parts = t.replace(',', '.').split(':').map(Number);
    return parts.reduce((acc, p) => acc * 60 + p, 0);
}

// ── Links to spin-off clips ──────────────────────────────────────────────────
// A full video lists the separately published clips that were marked in it while recording. The
// block is recognised by its heading line so it can be rewritten as clips get uploaded.
const CLIP_HEADINGS = ['🧩 Phần nền tách thành video riêng:', '🧩 Background explained in its own video:'];
export function mergeClipLinksIntoDescription(description: string, clips: { title: string; url: string }[], vn: boolean): string {
    const lines = (description || '').split('\n');
    const at = lines.findIndex(l => CLIP_HEADINGS.includes(l.trim()));
    let rest = lines;
    if (at !== -1) {
        let end = at + 1;
        while (end < lines.length && lines[end].trim().startsWith('• ')) end++;
        rest = [...lines.slice(0, at), ...lines.slice(end)];
    }
    const base = rest.join('\n').replace(/\n{3,}/g, '\n\n').trimEnd();
    if (clips.length === 0) return base;
    const block = [CLIP_HEADINGS[vn ? 0 : 1], ...clips.map(c => `• ${c.title.replace(/[<>]/g, '')}: ${c.url}`)].join('\n');
    return (base ? `${base}\n\n${block}` : block).slice(0, 5000);
}

// ── Links to other videos, added by hand ─────────────────────────────────────
const RELATED_HEADINGS = ['📺 Video liên quan:', '📺 Related videos:'];
const LINK_HEADINGS = [...CLIP_HEADINGS, ...RELATED_HEADINGS];
type LinkBlock = { at: number; end: number; heading: string; items: string[] };
function findLinkBlocks(lines: string[]): LinkBlock[] {
    const blocks: LinkBlock[] = [];
    for (let i = 0; i < lines.length; i++) {
        if (!LINK_HEADINGS.includes(lines[i].trim())) continue;
        let end = i + 1;
        while (end < lines.length && lines[end].trim().startsWith('• ')) end++;
        blocks.push({ at: i, end, heading: lines[i].trim(), items: lines.slice(i + 1, end).map(l => l.trim()) });
        i = end - 1;
    }
    return blocks;
}
/** The link blocks of a description (clip links and related-video links), as text, to carry over when the rest is regenerated. */
export function extractLinkBlocks(description: string): string {
    const lines = (description || '').split('\n');
    return findLinkBlocks(lines).filter(b => b.items.length > 0).map(b => [b.heading, ...b.items].join('\n')).join('\n\n');
}
/** Adds links to the "related videos" block, creating it at the end if needed. A URL already anywhere in the description is not added twice. */
export function addRelatedLinksToDescription(description: string, links: { title: string; url: string }[], vn: boolean): string {
    const fresh = links.filter((l, i) => !(description || '').includes(l.url) && links.findIndex(x => x.url === l.url) === i);
    if (fresh.length === 0) return description || '';
    const bullets = fresh.map(l => `• ${l.title.replace(/[<>]/g, '').replace(/\s+/g, ' ').trim()}: ${l.url}`);
    const lines = (description || '').split('\n');
    const block = findLinkBlocks(lines).find(b => RELATED_HEADINGS.includes(b.heading));
    if (block) return [...lines.slice(0, block.end), ...bullets, ...lines.slice(block.end)].join('\n').slice(0, 5000);
    const base = (description || '').trimEnd();
    const added = [RELATED_HEADINGS[vn ? 0 : 1], ...bullets].join('\n');
    return (base ? `${base}\n\n${added}` : added).slice(0, 5000);
}
